import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useCallback, useState } from 'react';
import { fetcher } from '@utils/data-fetching';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { sanitizeHtml } from '@utils/strings/sanitizeHtml';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

const AI_URL = process.env.NEXT_PUBLIC_AI_URL;

export interface CustomMessageData {
  customData?: {
    jobSearchResults?: {
      items: {
        id: number,
        score: number,
        data: ApiSchema<'JobItem'>
      }[],
      cursor: string,
      type: string,
    }
    resumeSearchRequest?: {
      keyword?: string,
      location?: string[],
      radiusInKm?: number,
      spokenLanguages?: string[],
      domains?: string[],
      driversLicense?: boolean,
      studyLevels?: string[],
      lastActivity?: number,
      careerLevels?: string[]
    }
    resumeSearchResults?: {
      items: ApiSchema<'ResumeCardData'>[],
      pages: number,
    }
  }
}

export interface Message {
  id: string;
  type: string;
  content: string;
  name?: string;
  keyword?: string;
  response_metadata?:CustomMessageData;
}

export interface Suggestions {
  data: string[];
  type: string;
  id: string;
  retry?: boolean;
}

export interface Filters {
  value: string;
  type: string;
}

export interface MessageMetadata {
  run_id?: string;
}

export interface MessageHistoryResponse {
  messages: Message[];
  created_at: string;
}

export interface StreamState {
  status: 'inflight' | 'error' | 'done';
  messages?: Message[];
  run_id?: string;
}

export interface StreamStateProps {
  stream: StreamState | null;
  filters: Filters | null;
  startStream: (
    input: Message,
    thread_id: string,
    config?: Record<string, unknown>,
  ) => Promise<void>;
  stopStream?: (clear?: boolean) => void;
  initializeWithHistory: (
    name: string,
    job?: string,
    callback?: () => void | Promise<void>,
  ) => Promise<MessageHistoryResponse>;
  isLoadingHistory: boolean;
  biaSuggestions?: Suggestions;
  clearBiaSuggestions: () => void;
  resetSearchFilters: () => void;
}

export function mergeMessagesById(
  left: Message[] | Record<string, Message[]> | null | undefined,
  right: Message[] | Record<string, Message[]> | null | undefined,
): Message[] {
  const leftMessages = Array.isArray(left) ? left : left?.messages;
  const rightMessages = Array.isArray(right) ? right : right?.messages;

  const merged = (leftMessages ?? [])?.slice();
  (rightMessages || []).forEach((msg) => {
    const foundIdx = merged.findIndex((m) => m.id === msg.id);
    if (foundIdx === -1) {
      merged.push(msg);
    } else {
      merged[foundIdx] = msg;
    }
  });

  return merged;
}

export function useStreamState(api: string): StreamStateProps {
  const [current, setCurrent] = useState<StreamState | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestions | undefined>(undefined);
  const [controller, setController] = useState<AbortController | null>(null);
  const anonSessionId = useAnonSessionId();
  const [filters, setFilters] = useState<Filters | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);

  const {
    router, locale, pathname, defaultLocale,
  } = useExtendedRouter();


  const resetSearchFilters = () => {
    setFilters(null);
  };

  const fetchHistory = useCallback(async (name: string, job?: string, callback?: () => void | Promise<void>) => {
    try {
      setIsLoadingHistory(true);
      const response = await fetcher<MessageHistoryResponse>(
        AI_URL + (name ? `/history/v2/${name}${job ? `?job=${job}` : ''}` : '/chat/history'),
        undefined,
        {
          headers: {
            'x-anon-session-id': anonSessionId,
          },
        },
      );
      if (!response) {
        throw new Error('Failed to fetch history');
      }

      const historyMessages: Message[] = response.messages;

      setCurrent((prevCurrent) => ({
        status: prevCurrent?.status || 'done',
        messages: mergeMessagesById(prevCurrent?.messages, historyMessages),
        run_id: prevCurrent?.run_id,
      }));

      if (callback) {
        await callback();
      }

      return response;
    } catch (error) {
      console.error('Error fetching history', error);
      throw error;
    } finally {
      setIsLoadingHistory(false);
    }
  }, [anonSessionId]);

  const startStream = useCallback(
    async (
      input: Message,
      name: string,
      config?: {
        headers?: Record<string, string>;
        files?: File[];
        configurations?: Record<string, string>;
      },
    ) => {
      const abortController = new AbortController();
      setController(abortController);

      const inputMessages: Message[] = [];
      if (input) {
        if (Array.isArray(input)) {
          const sanitized = input.map((msg: Message) => ({
            ...msg,
            content: sanitizeHtml(msg?.content),
          }));
          inputMessages.push(...sanitized);
        } else {
          const sanitized = {
            ...input,
            content: sanitizeHtml(input.content),
          };
          inputMessages.push(sanitized);
        }
      }

      setCurrent((prevCurrent) => ({
        status: 'inflight',
        messages: [...(prevCurrent?.messages || []), ...inputMessages],
        run_id: prevCurrent?.run_id,
      }));

      const formData = new FormData();
      formData.append('input', sanitizeHtml(input.content));

      if (config?.files) {
        config.files.forEach((file) => {
          formData.append('files', file);
        });
      }
      if (config?.configurations) {
        formData.append(
          'configurations',
          JSON.stringify(config.configurations),
        );
      }

      await fetchEventSource(api + (name ? `/${name}` : ''), {
        signal: abortController.signal,
        method: 'POST',
        headers: config?.headers,
        credentials: 'include',
        body: formData,
        openWhenHidden: true,
        onopen: async (response) => {
          if (
            response.ok
            && response.headers.get('content-type') === 'text/event-stream'
          ) {
            // Connection established
          } else if (response.status === 402) {
            throw new Error('Subscription required');
          } else if (
            response.status >= 400
            && response.status < 500
            && response.status !== 429
          ) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        },
        onmessage(msg) {
          if (msg.event === 'data') {
            const messages: Message[] | Record<string, Message[]> = JSON.parse(
              msg.data,
            ) as Message[] | { messages: Message[] };

            // If last message from stream contains job results, redirect to homepage
            // This is a temp solution since BiaDropdown doesn't show job cards
            const newMessages = Array.isArray(messages) ? messages : messages?.messages;
            const homepagePath = locale === defaultLocale ? '/' : `/${locale}`;
            if (pathname !== homepagePath && newMessages?.at(-1)?.response_metadata?.customData?.jobSearchResults) {
              router.push('/');
              return;
            }

            setCurrent((prevCurrent) => ({
              status: 'inflight',
              messages: mergeMessagesById(prevCurrent?.messages, messages),
              run_id: prevCurrent?.run_id,
            }));
          } else if (msg.event === 'resume_search_filter') {
            setFilters({
              type: 'resume_search_filter',
              value: msg.data,
            });
          } else if (msg.event === 'metadata') {
            const metadata: MessageMetadata = JSON.parse(
              msg.data,
            ) as MessageMetadata;

            setCurrent((prevCurrent) => ({
              status: 'inflight',
              messages: prevCurrent?.messages,
              run_id: metadata.run_id || prevCurrent?.run_id,
            }));
          } else if (msg.event === 'job_search_intent') {
            const message = JSON.parse(msg.data) as Message;
            const { keyword } = message;
            const jobsPath = '/job/list';
            const path = keyword?.trim() ? `${jobsPath}/${keyword}` : jobsPath;
            const href = getI18nLinkHref(path, locale);
            void router.push(href, { withLocale: false });
          } else if (msg.event === 'suggestions') {
            const suggestionsData: Suggestions = JSON.parse(
              msg.data,
            ) as Suggestions;

            setSuggestions(suggestionsData);
          } else if (msg.event === 'error') {
            setCurrent((prevCurrent) => ({
              status: 'error',
              messages: prevCurrent?.messages,
              run_id: prevCurrent?.run_id,
            }));
          }
        },
        onclose() {
          setCurrent((prevCurrent) => ({
            status:
              prevCurrent?.status === 'error' ? prevCurrent.status : 'done',
            messages: prevCurrent?.messages,
            run_id: prevCurrent?.run_id,
          }));
          setController(null);
        },
        onerror(error) {
          setCurrent((prevCurrent) => ({
            status: 'error',
            messages: prevCurrent?.messages,
            run_id: prevCurrent?.run_id,
          }));
          setController(null);
          throw error;
        },
      });
    },
    [api, locale, router, pathname, defaultLocale],
  );

  const stopStream = useCallback(
    (clear: boolean = false) => {
      controller?.abort();
      setController(null);
      if (clear) {
        setCurrent((prevCurrent) => ({
          status: 'done',
          run_id: prevCurrent?.run_id,
        }));
      } else {
        setCurrent((prevCurrent) => ({
          status: 'done',
          messages: prevCurrent?.messages,
          run_id: prevCurrent?.run_id,
        }));
      }
    },
    [controller],
  );

  const clearBiaSuggestions = useCallback(
    () => {
      setSuggestions(undefined);
    },
    [],
  );

  return {
    startStream,
    stopStream,
    stream: current,
    initializeWithHistory: fetchHistory, // Include the new method
    isLoadingHistory,
    biaSuggestions: suggestions,
    filters,
    resetSearchFilters,
    clearBiaSuggestions,
  };
}
